import { AbstractControl, FormGroup } from '@angular/forms';
import {
  DirectusCondition,
  DirectusFieldItemTranslations,
  DirectusOperator,
  DirectusReportFieldItem,
  DirectusRule,
  ReportFormField,
} from '@interfaces';

// Fields that should not be asked on report creation
const ignoredFields = ['id', 'user_created', 'date_created', 'user_updated', 'date_updated'];

export function processFields(directusFields: DirectusReportFieldItem[]): ReportFormField[] {
  return directusFields
    .filter((field) => !ignoredFields.includes(field.field))
    .map((field) => ({
      name: field.field,
      label: field.meta.translations ?? {
        language: 'en-US',
        translation: field.field
          .replace(new RegExp('_', 'g'), ' ')
          .replace(
            /\w\S*/g,
            (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
          ),
      },
      type: field.type,
      interface: field.meta.interface,
      default_value:
        field.meta.interface === 'datetime'
          ? field.schema?.default_value ?? new Date()
          : field.schema?.default_value,
      max_length: field.schema?.max_length,
      numeric_precision: field.schema?.numeric_precision,
      numeric_scale: field.schema?.numeric_scale,
      readonly: field.meta.readonly,
      hidden: field.meta.hidden,
      width: field.meta.width,
      note: field.meta.note,
      conditions: field.meta.conditions,
      required: field.meta.required,
      group: field.meta.group,
      sort: field.meta.sort,
      options: field.meta.options,
    }));
}

function evaluateRule(
  rule: DirectusRule,
  fieldValueGetter: (fieldName: string) => any, // eslint-disable-line @typescript-eslint/no-explicit-any
): boolean {
  // Handle AND conditions
  if (rule._and) {
    return rule._and.every((r) => evaluateRule(r, fieldValueGetter));
  }

  // Handle OR conditions
  if (rule._or) {
    return rule._or.some((r) => evaluateRule(r, fieldValueGetter));
  }

  // Handle leaf conditions (field comparisons)
  const fieldName = Object.keys(rule).find((k) => !k.startsWith('_'));
  if (!fieldName) return false;

  const operators = rule[fieldName] as { [op in DirectusOperator]?: any }; // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!operators) return false;

  const fieldValue = fieldValueGetter(fieldName);

  // Evaluate each operator
  return Object.entries(operators).every(([op, expectedValue]) => {
    switch (op as DirectusOperator) {
      case '_eq':
        return fieldValue === expectedValue;
      case '_neq':
        return fieldValue !== expectedValue;
      case '_contains':
        return String(fieldValue).includes(String(expectedValue));
      case '_ncontains':
        return !String(fieldValue).includes(String(expectedValue));
      case '_empty':
        return !fieldValue || (Array.isArray(fieldValue) && !fieldValue.length);
      case '_nempty':
        return fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0);
      case '_in':
        return Array.isArray(expectedValue) && expectedValue.includes(fieldValue);
      case '_nin':
        return Array.isArray(expectedValue) && !expectedValue.includes(fieldValue);
      case '_gt':
        return fieldValue > expectedValue;
      case '_gte':
        return fieldValue >= expectedValue;
      case '_lt':
        return fieldValue < expectedValue;
      case '_lte':
        return fieldValue <= expectedValue;
      default:
        return false;
    }
  });
}

export function evaluateCondition(
  condition: DirectusCondition,
  fieldValueGetter: (fieldName: string) => any, // eslint-disable-line @typescript-eslint/no-explicit-any
): boolean {
  if (!condition.rule) return false;
  return evaluateRule(condition.rule, fieldValueGetter);
}

export function convertSentenceCaseToSnakeCase(text: string, upper: boolean = false) {
  if (upper) {
    return text.toUpperCase().replace(new RegExp(' ', 'g'), '_');
  }

  return text.toLowerCase().replace(new RegExp(' ', 'g'), '_');
}

export function convertSnakeCaseToSentenceCase(text: string) {
  return text
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getTranslatedLabel(
  translationArray: DirectusFieldItemTranslations[] | DirectusFieldItemTranslations,
) {
  if (!Array.isArray(translationArray)) {
    return 'translationArray';
  }

  // Directus only supports a limited number of languages.
  // Use local translations instead.
  // Values for the field names in key 'en-US' will be the translation keys in Directus.
  const directusLanguageCode = 'en-US';

  // The translation language in the translation array (Directus) can vary
  // While the actual selected language in the languageService may be 'en', 'es', 'ar', 'fr', 'ht'
  const translation = translationArray.find(
    (labelTranslation) => labelTranslation.language === directusLanguageCode,
  )?.translation;

  return (
    translation || ''
    // || translationArray.find((t) => t.language === 'en-US')?.translation
  );
}

export function findControl(name: string, group: FormGroup): AbstractControl | null {
  if (group.contains(name)) return group.get(name);

  for (const [, control] of Object.entries(group.controls)) {
    if (control instanceof FormGroup) {
      const found = findControl(name, control);
      if (found) return found;
    }
  }
  return null;
}

export function getFieldValue(fieldName: string, group: FormGroup): any {
  const control = findControl(fieldName, group);
  return control?.value;
}

export function isHidden(field: ReportFormField, group: FormGroup): boolean {
  if (!field.conditions?.length) return field.hidden ?? false;

  return field.conditions.some(
    (condition) =>
      condition.hidden && evaluateCondition(condition, (name) => getFieldValue(name, group)),
  );
}

export function isRequired(field: ReportFormField, group: FormGroup): boolean {
  if (field.required) return true;
  if (!field.conditions?.length) return false;

  return field.conditions.some(
    (condition) =>
      condition.required && evaluateCondition(condition, (name) => getFieldValue(name, group)),
  );
}

export function getPlaceholder(field: ReportFormField, group: FormGroup): string {
  if (!field.conditions?.length) return field.options?.placeholder ?? '';

  // Find first matching condition with a placeholder
  const matchingCondition = field.conditions.find(
    (condition) =>
      condition.options?.placeholder &&
      evaluateCondition(condition, (name) => getFieldValue(name, group)),
  );

  return matchingCondition?.options?.placeholder ?? field.options?.placeholder ?? '';
}
